/**
 * Decides when a Team Chat search is really someone trying to reach Lodgeic.
 *
 * Lodgeic staff are not reachable in Team Chat — customers get support through
 * the Intercom messenger. So when a search (conversation list or the people
 * picker) looks like "lodgeic", "support", "password reset", "it's broken" and
 * so on, the chat shows a card that opens support chat instead.
 *
 * Two strengths:
 *   strong — the card leads; any real matches are still listed below it, so a
 *            hotel search that happens to say "error" loses nothing.
 *   soft   — a quiet "Ask support" row after the results.
 *
 * Hotel vocabulary that sits one letter from "lodgeic" (lodge, lodging,
 * lodged, lodgers) never triggers, so "River Lodge" stays a normal search.
 */

export const SUPPORT_TOPICS = {
  lodgeic: {
    title: 'Trying to reach Lodgeic?',
    body: 'The Lodgeic team isn’t in Team Chat. Support chat reaches them directly.',
  },
  support: {
    title: 'Looking for support?',
    body: 'Team Chat is for people at your properties. For help with Lodgeic itself, use support chat.',
  },
  signin: {
    title: 'Trouble signing in?',
    body: 'Support chat can help with sign-in, passwords and MFA codes.',
  },
  broken: {
    title: 'Something not working?',
    body: 'Tell support what happened. A screenshot helps.',
  },
  account: {
    title: 'Question about your Lodgeic account?',
    body: 'Billing, plans and user seats are handled in support chat.',
  },
  howto: {
    title: 'Question about using Lodgeic?',
    body: 'Support chat can walk you through it.',
  },
};

// Order matters: the first group with a hit wins.
const GROUPS = [
  {
    topic: 'lodgeic',
    strength: 'strong',
    terms: ['lodgeic team', 'lodgeic staff', 'account manager', 'customer success', 'csm'],
  },
  {
    topic: 'support',
    strength: 'strong',
    terms: [
      'support', 'help', 'helpdesk', 'help desk', 'customer service', 'tech support',
      'contact us', 'talk to someone', 'intercom', 'live chat',
    ],
  },
  {
    topic: 'signin',
    strength: 'strong',
    terms: [
      'password', 'reset password', 'login', 'log in', 'sign in', 'signin', 'locked out',
      'mfa', '2fa', 'verification code', 'authenticator',
    ],
  },
  {
    topic: 'broken',
    strength: 'strong',
    terms: [
      'bug', 'broken', 'not working', 'doesnt work', 'error', 'crash', 'glitch',
      'not loading', 'wont load',
    ],
  },
  {
    topic: 'account',
    strength: 'strong',
    terms: ['billing', 'subscription', 'pricing', 'seats', 'renewal'],
  },
  {
    topic: 'howto',
    strength: 'soft',
    terms: [
      'how do i', 'how to', 'training', 'onboarding', 'tutorial', 'feature request',
      'integration', 'missing data',
    ],
  },
];

const HOTEL_WORD = /^lodg(e|es|ed|er|ers|ing|ings)?$/;

/** Levenshtein distance; inputs are single short words. */
export function editDistance(a, b) {
  const rows = a.length + 1;
  const cols = b.length + 1;
  const d = Array.from({ length: rows }, (_, i) => [i]);
  for (let j = 1; j < cols; j += 1) d[0][j] = j;
  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < cols; j += 1) {
      d[i][j] = Math.min(
        d[i - 1][j] + 1,
        d[i][j - 1] + 1,
        d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
  }
  return d[a.length][b.length];
}

/** "lodgeic", "@lodgeic.io", "lodgic", "logeic"… but not lodge/lodging/lodged. */
export function looksLikeLodgeic(word) {
  if (!word || word.length < 5) return false;
  if (HOTEL_WORD.test(word) || word.startsWith('lodgin')) return false;
  if (word.startsWith('lodgei')) return true;
  return editDistance(word, 'lodgeic') <= (word.length >= 7 ? 2 : 1);
}

const wordsOf = (text) => String(text || '')
  .toLowerCase()
  .replace(/[’']/g, '')
  .replace(/[^a-z0-9@.\s-]/g, ' ')
  .split(/[\s@.-]+/)
  .filter(Boolean);

/**
 * @param {string} query    what the person typed
 * @param {object} options
 * @param {string[]} options.staffNames  display names of Lodgeic staff (hidden from the picker)
 * @returns {null | { topic: string, strength: 'strong'|'soft', term: string }}
 */
export function classifySupportQuery(query, { staffNames = [] } = {}) {
  const words = wordsOf(query);
  if (!words.length) return null;

  const lodgeicWord = words.find(looksLikeLodgeic);
  if (lodgeicWord) return { topic: 'lodgeic', strength: 'strong', term: lodgeicWord };

  // A Lodgeic person's name: whole words, or a 4+ letter start of the last word typed.
  const last = words[words.length - 1];
  for (const name of staffNames) {
    const parts = wordsOf(name);
    if (!parts.length) continue;
    const hit = words.some((w) => parts.includes(w))
      || (last.length >= 4 && parts.some((p) => p.startsWith(last)));
    if (hit) return { topic: 'lodgeic', strength: 'strong', term: String(name).trim() };
  }

  const padded = ` ${words.join(' ')} `;
  for (const group of GROUPS) {
    for (const term of group.terms) {
      const whole = padded.includes(` ${term} `) || padded.includes(` ${term}s `);
      // As-you-type: a partial last word of 4+ letters counts ("supp", "passw").
      const typing = !term.includes(' ') && last.length >= 4 && term.startsWith(last);
      if (whole || typing) return { topic: group.topic, strength: group.strength, term };
    }
  }
  return null;
}

